import { Ref, ref, reactive, watch } from 'vue';
import { searchPublications, getPublicationTags } from '@/services/data/publication';
import { writeUrlRequest } from '@/config/common';

type PublicationTagType = {
  documentMap: Record<string, string>
  subjectMap: Record<string, string>
  mainTypeMap: Record<string, string>
};
/* get Tag API return */
type PublicationTallyType = {
  tags: PublicationTagType,
  year: Record<number, number>
};
/* indiviudal API publication object */
export class PublicationItem {
  id?: string;
  displayDate?: string;
  description?: string;
  authors?: string[];
  fileName?: string;
  fullTitle?: string;
  doi?: string;
  image?: string; // original path
  imagePath?: string; // rendition path
  isFeatured?: boolean;
}
export type PublicationItemType = typeof PublicationItem;
/* API return object */
type PublicationQueryResultType = {
  subject?: Record<number, number>,
  // region: t.union([RegionTS, t.undefined]),
  documentType?: Record<number, number>,
  totalPage?: number,
  totalRecord?: number,
  items?: PublicationItemType[]
};
/* UI Stats */
type PublicationStatsType = {
  subject?: Record<number, number>,
  // region: t.union([RegionTS, t.undefined]),
  documentType?: Record<number, number>,
  page?: {
    totalPage?: number,
    startPage?: number,
    recordPerPage?: number
    totalRecord?: number
  }
};
/* UI Query */
export type QueryParameterType = {
  rootPath?: string | null,
  documentType?: Array<number> | null,
  subject?: Array<number> | null,
  searchText?: string | null, // UI use this
  searchKey?: Array<string> | null, // API  used this
  itemPerPage?: number,
  tag?: string | null,
  author?: string | null,
  year?: number | null,
  page?: number | null, /* start Page */
  sortBy?: number | null, /* sorting order, by default newest to oldest */
};

const appState = reactive({
  inFilterTerm: '', /* this is for holding user input, before they click enter or search button, just to save api calls when users enter partial words */
  pageNumber: 1,
  recordPerPage: 12
});

const defaultQueryParam:QueryParameterType = {
  rootPath: '/publications',
  documentType: [],
  subject: [],
  searchText: null,
  itemPerPage: 12,
  page: 1, /* start Page */
  sortBy: 1 /* sorting order, by default newest to oldest */
};
export type requestKeys = keyof QueryParameterType;
/**
 * read query parameters from url passing request string
 * @returns query object
 */
function readRequest ():QueryParameterType {
  const requestParam:QueryParameterType = { ...defaultQueryParam };
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  requestParam.documentType = urlParams.get('documentType')?.split(',').filter(v => !!v).map(v => +v);
  requestParam.subject = urlParams.get('subject')?.split(',').filter(v => !!v).map(v => +v);
  requestParam.searchText = urlParams.get('searchText') ?? null;
  requestParam.tag = urlParams.get('tag') ?? null;
  requestParam.author = urlParams.get('author') ?? null;
  requestParam.page = +(urlParams.get('page') ?? 1);
  requestParam.sortBy = +(urlParams.get('sortBy') ?? 1);
  if (urlParams.get('year') != null) {
    requestParam.year = parseInt(urlParams.get('year') ?? '0');
  }
  return requestParam;
}

export function useAppState () {
  return appState;
}

function app () {
  const state = useAppState();
  const publicationList: Ref<PublicationItemType[] | undefined> = ref([]);
  const queryParam:Ref<QueryParameterType> = ref(readRequest()); /** clone from default */
  const publicationStats:Ref<PublicationStatsType> = ref({});

  const tagOptions = ref();
  const yearOptions = ref();
  // Init loads anything needed at startup
  const init = async () => {
    const data:PublicationTallyType = await getPublicationTags();
    tagOptions.value = data.tags;
    const documentTypeOption: Record<string, Record<string, string>[]> = {};
    Object.keys(data.tags.documentMap).forEach(key => {
      const labels = data.tags.documentMap[key].split(' - ');
      if (!documentTypeOption[labels[0]]) {
        documentTypeOption[labels[0]] = [];
      }
      documentTypeOption[labels[0]].push({ value: key, label: labels[1] });
    });
    tagOptions.value.documentTypeOption = documentTypeOption;
    tagOptions.value.yearOptions = {};
    Object.keys(data?.year).forEach(x => { tagOptions.value.yearOptions[x] = x; });
    await query();
  };

  const clearAllFilter = () => {
    queryParam.value = { ...defaultQueryParam };
  };
  /* remove on value from array */
  const removeSingleQueryItem = (list:Array<number> | null, index:number) => {
    if (list) {
      list?.splice(index, 1);
    }
  };
  /* clear the whole query item, if it is array set it as empty, */
  const clearQueryItem = (fieldKey:requestKeys) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (queryParam.value[fieldKey] as any) = null;
  };
  const updatePage = (pageNumber:number) => {
    queryParam.value.page = Math.max(1, pageNumber);
  };

  watch(queryParam, () => {
    query();
  }, { deep: true });

  const query = async () => {
    writeUrlRequest(queryParam.value, ['itemPerPage', 'rootPath', 'searchKey', 'subject', 'documentType', 'searchText', 'tag', 'author', 'page', 'sortBy', 'year']);
    const data = await searchPublications(queryParam.value) as PublicationQueryResultType;
    publicationList.value = data.items;
    publicationStats.value.documentType = data.documentType;
    publicationStats.value.subject = data.subject;
    publicationStats.value.page = {
      startPage: queryParam.value?.page ?? 1,
      totalPage: data.totalPage,
      totalRecord: data.totalRecord ?? 0,
      recordPerPage: queryParam.value?.itemPerPage ?? 20
    };
  };

  init();
  return {
    state,
    tagOptions,
    yearOptions,
    queryParam,
    updatePage,
    removeSingleQueryItem,
    clearQueryItem,
    clearAllFilter,
    query,
    publicationList,
    publicationStats

  };
}
const appSingleton = app();
export function publicationSearchState () {
  // NOTE: Any setup hooks must be called here.
  // This gets called from every setup() that uses the app.
  return appSingleton;
}
